/* globals gtag */
import React, { createContext, useContext, useState, useCallback } from 'react';

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
    const [currentPage, setCurrentPage] = useState('paintings');
    const [menuState, setMenuState] = useState(false);
    const [imageView, setImageView] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [screenType, setScreenType] = useState('desktop');
    const [paintings, setPaintingsState] = useState({});
    const [graphics, setGraphicsState] = useState({});

    const toggleMenu = useCallback(() => {
        setMenuState(prev => {
            const newState = !prev;
            if (newState && typeof gtag !== 'undefined') {
                gtag('event', 'menu open', {
                    event_category: 'menu'
                });
            }
            return newState;
        });
    }, []);

    const setPage = useCallback((pageName) => {
        setMenuState(false);
        setCurrentPage(pageName);
    }, []);

    const setPaintings = useCallback((list) => {
        setPaintingsState({
            leftMost: 0,
            list: list
        });
        setCurrentPage('paintings');
    }, []);

    const setGraphics = useCallback((list) => {
        setGraphicsState({
            leftMost: 0,
            list: list
        });
        setCurrentPage('graphics');
    }, []);

    const showImageView = useCallback((image) => {
        setImageView(true);
        setSelectedImage(image);
        if (typeof gtag !== 'undefined') {
            gtag('event', 'image preview', {
                event_category: 'image',
                event_label: image.title
            });
        }
    }, []);

    const hideImageView = useCallback(() => {
        setImageView(false);
        setSelectedImage(null);
    }, []);

    const changeScreenType = useCallback((type) => {
        setScreenType(type);
    }, []);

    const scrollLeft = useCallback(() => {
        const updateList = (prevList) => {
            if (prevList.leftMost > 0) {
                const nextImage = screenType === 'mobile' ? prevList.leftMost - 1 : prevList.leftMost - 3;
                if (typeof gtag !== 'undefined') {
                    gtag('config', 'UA-162933668-1', { page_path: `/${currentPage}/${nextImage}` });
                }
                return { ...prevList, leftMost: nextImage };
            }
            return prevList;
        };

        if (currentPage === 'paintings') {
            setPaintingsState(updateList);
        } else if (currentPage === 'graphics') {
            setGraphicsState(updateList);
        }
    }, [currentPage, screenType]);

    const scrollRight = useCallback(() => {
        const updateList = (prevList) => {
            if (prevList.leftMost < prevList.list.length - 1) {
                const nextImage = screenType === 'mobile' ? prevList.leftMost + 1 : prevList.leftMost + 3;
                if (typeof gtag !== 'undefined') {
                    gtag('config', 'UA-162933668-1', { page_path: `/${currentPage}/${nextImage}` });
                }
                return { ...prevList, leftMost: nextImage };
            }
            return prevList;
        };

        if (currentPage === 'paintings') {
            setPaintingsState(updateList);
        } else if (currentPage === 'graphics') {
            setGraphicsState(updateList);
        }
    }, [currentPage, screenType]);

    const store = {
        currentPage,
        menuState,
        imageView,
        selectedImage,
        screenType,
        paintings,
        graphics,
        toggleMenu,
        setPage,
        setPaintings,
        setGraphics,
        showImageView,
        hideImageView,
        scrollLeft,
        scrollRight,
        changeScreenType
    };

    return (
        <StoreContext.Provider value={store}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => useContext(StoreContext);
